import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import { PieChart } from '../../../Charts/PieChart';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import { useEffect, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import './Renewals.scss';
import { Pagination } from '@lmig/lmds-react-pagination';
import { Link } from '@lmig/lmds-react-link';
import { getPLPSTLink, getPLMMRLink, getRecentActivitiesLink } from 'Foundation/Configurations/GlobalConfiguration';
import {
  RenewalsSortableKeys,
  IRenewalsTableData,
  IRenewalsDocument,
  IRenewalsDocumentCustomer,
  SortDirection,
  SortDirections,
} from '../../../Models/RenewalsModels';
import {
  IconLandlord,
  IconHome,
  IconAuto,
  IconMotorcycle,
  IconLandlordCondo,
  IconEarthquake,
  IconWatercraft,
  IconUmbrella,
  IconRenters,
  IconCondo,
  IconInfo,
} from '@lmig/lmds-react-icons';
import { useLocalTabDataHandler, APIList } from '../../../Data/useLocalTabDataHandler';
import { useUserData } from 'Foundation/User/UserContext';
import { toTitleCase } from 'Utilities/StringFormatter';
import { Tooltip } from '@lmig/lmds-react-tooltip';
import { StringHelper } from 'Utilities/StringHelper';

const GetPolicyIcon = ({ policy }: { [key: string]: IRenewalsTableData }) => {
  const iconMap: { [key: string]: any } = {
    home: <IconHome size="16" />,
    renters: <IconRenters size="16" />,
    condo: <IconCondo size="16" />,
    landlord: <IconLandlord size="16" />,
    'landlord condo': <IconLandlordCondo size="16" />,
    eq: <IconEarthquake size="16" />,
    auto: <IconAuto size="16" />,
    motorcycle: <IconMotorcycle size="16" />,
    umbrella: <IconUmbrella size="16" />,
    watercraft: <IconWatercraft size="16" />,
  };
  return iconMap[policy.product.toLowerCase()] ?? <></>;
};

const formatRenewalChangeAmount = (amount: string) => {
  const numericalAmount = Number(amount);
  return `${numericalAmount > 0 ? '+' : ''}${numericalAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })}`;
};

const formatRenewalPercentage = (percentage: string) => {
  const numericalPercentage = Number(percentage);
  return `${numericalPercentage > 0 ? '+' : ''}${numericalPercentage}`;
};

const formatEffectiveDate = (date: string) => `${date.slice(5, 7)}/${date.slice(8)}/${date.slice(0, 4)}`;

const getRenewalColor = (renewalChangePercentage: string) => {
  const numericalAmount = Number(renewalChangePercentage);
  if (numericalAmount < 0) return 'green-renewal';
  if (numericalAmount >= 20) return 'red-renewal';
  return '';
};

const Loading = () => (
  <div className="spinner-container">
    <div className="spinner">
      <span className="spinner__sr">Loading…</span>
    </div>
  </div>
);

export default function Renewals(props: { activeTab: number }) {
  const { userData } = useUserData();
  const [sortDirections, setSortDirections] = useState<SortDirections>({
    [RenewalsSortableKeys.renewalChangePercentage]: SortDirection.none,
    [RenewalsSortableKeys.product]: SortDirection.none,
    [RenewalsSortableKeys.customer]: SortDirection.none,
    [RenewalsSortableKeys.customerLastName]: SortDirection.none,
    [RenewalsSortableKeys.effective]: SortDirection.ascending,
  });
  const [currentSortKey, setCurrentSortKey] = useState<RenewalsSortableKeys | undefined>(
    RenewalsSortableKeys.effective,
  );
  const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection>(SortDirection.ascending);

  const [initialDataLoad, setInitialDataLoad] = useState<IRenewalsTableData[]>([]);
  const [renewalsTableData, setRenewalsTableData] = useState<IRenewalsTableData[]>([]);
  const [renewalsTableDataToDisplay, setRenewalsTableDataToDisplay] = useState<IRenewalsTableData[]>([]);
  const [page, setPage] = useState(0);
  const [renewalDocumentCount, setRenewalDocumentCount] = useState(0);
  const [greaterThan20, setGreaterThan20] = useState(0);
  const [lessThan20, setLessThan20] = useState(0);
  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });
  const { data, error, isLoading } = useLocalTabDataHandler({
    api: APIList.renewals,
    body: JSON.stringify({
      policyTypes: ['RENEWAL'],
      sortColumn: currentSortKey,
      SortDirection: currentSortDirection,
    }),
  });

  const recentActivitiesLink = getRecentActivitiesLink();

  const LMDSPagination = () => {
    const itemCount = renewalsTableData?.length;
    const itemsPerPage = 5;

    const changeHandler = (e: any) => {
      setPage(e.page);
    };

    if (itemCount > itemsPerPage)
      return (
        <Pagination
          id="renewals-pagination"
          className="recent-activities-pagination"
          withWords={false}
          itemCount={itemCount}
          onChange={changeHandler}
          showItemsPerPage={false}
          page={page}
          itemsPerPage={itemsPerPage}
        />
      );
    else return <></>;
  };

  useEffect(() => {
    const getRenewalChangeAmount = (obj: IRenewalsDocument) =>
      obj?.totalRenewalPremium && obj?.previousTermTotalPremium
        ? (obj?.totalRenewalPremium - obj?.previousTermTotalPremium).toFixed(2)
        : 'Not Available';

    const getCustomerName = (customer: IRenewalsDocumentCustomer) => {
      if (customer) {
        if (StringHelper.hasNonEmptyString(customer.firstName) || StringHelper.hasNonEmptyString(customer.lastName)) {
          return toTitleCase(`${customer.firstName ? customer.firstName : ''} ${customer.lastName ? customer.lastName : ''}`.trim());
        }
        else if (StringHelper.hasNonEmptyString(customer.firstName)) {
          return toTitleCase(customer.insuredName);
        }
      }
      return 'Not Available';
    };

    const getCustomerLastName = (customer: IRenewalsDocumentCustomer) => {
      if (customer) {
        if (StringHelper.hasNonEmptyString(customer.lastName)) {
          return toTitleCase(customer.lastName);
        } 
        else if (StringHelper.hasNonEmptyString(customer.insuredName)) {
          return toTitleCase(customer.insuredName);
        }
      }
      return 'Not Available';
    };

    const renewalToTableDataMapper = (renewals: IRenewalsDocument[]) => {
      return renewals.map((renewal: IRenewalsDocument) => ({
        customer: getCustomerName(renewal.customers[0]),
        customerLastName: getCustomerLastName(renewal.customers[0]),
        renewalChangeAmount: getRenewalChangeAmount(renewal),
        renewalChangePercentage: parseFloat(renewal?.percentChangeInPremium).toFixed(1),
        effective: renewal?.termPolicyEffectiveDate,
        policyNumber: renewal?.policyNumber,
        product: renewal?.product,
      })) as IRenewalsTableData[];
    };

    const isValidRenewalPolicy = (renewal: IRenewalsDocument) => renewal.transactionType === 'RENEWAL';

    const handlePolicyData = () =>
      new Promise((resolve, reject) => {
        if (isLoading) resolve('loading');
        if (error) reject();
        if (!data?.length) reject();

        let json = data?.policyDocumentList;
        json = json.filter(isValidRenewalPolicy);

        if (!json?.length) reject('no valid policies to display');

        const mappedData = renewalToTableDataMapper(json);

        setRenewalDocumentCount(data?.renewalDocumentCount ?? 0);
        setGreaterThan20(data?.renewalPercentChangeAtOrAboveThresholdCount ?? 0);
        setLessThan20(data?.renewalPercentChangeBelowThresholdCount ?? 0);
        setInitialDataLoad(mappedData);
        resolve(data);
      });

    handlePolicyData().catch(() => {
      GetNoDataTile('renewals', setNoDataTile);
    });
  }, [data, error, isLoading]);

  const sortTableData = (renewalTable: Array<IRenewalsTableData> | undefined) => {
    let sortedRenewalTableData: Array<IRenewalsTableData> = [];
    const isValueForKeyNotNull = (key: string) => sortDirections[key as RenewalsSortableKeys] !== SortDirection.none;
    const key = Object.keys(sortDirections).find(isValueForKeyNotNull) as RenewalsSortableKeys;
    const switchValues = (a: Array<any>) => a.slice().reverse();
    const compareValues = (valuesToCompare: Array<any>, type: string) => {
      if (type === 'number') return Number(valuesToCompare[0]) - Number(valuesToCompare[1]);
      else if (type === 'string') return valuesToCompare[0].localeCompare(valuesToCompare[1]);
      else {
        return null;
      }
    };

    if (renewalTable !== undefined && renewalTable?.length > 0) {
      sortedRenewalTableData = renewalTable.sort((a: IRenewalsTableData, b: IRenewalsTableData) => {
        let valuesToCompare = [a[key], b[key]];
        
        if (key === RenewalsSortableKeys.customerLastName) {
          const aValue = !a[key] ? a[RenewalsSortableKeys.customer] : a[key];
          const bValue = !b[key] ? b[RenewalsSortableKeys.customer] : b[key];
          
          valuesToCompare = [aValue, bValue];
        }

        if (sortDirections[key] === SortDirection.descending) valuesToCompare = switchValues(valuesToCompare);
        if (key === RenewalsSortableKeys.effective) valuesToCompare = switchValues(valuesToCompare);
        return compareValues(valuesToCompare, 'string');
      });
    }

    return sortedRenewalTableData;
  };

  useEffect(() => {
    let sortedTableData = sortTableData(initialDataLoad);
    setRenewalsTableData(sortedTableData);
    setPage(0);
  }, [sortDirections, initialDataLoad]);

  useEffect(() => {
    setRenewalsTableDataToDisplay(renewalsTableData.slice(0, 5));
  }, [renewalsTableData]);

  const renewalTableSorter = (key: RenewalsSortableKeys) => {
    const getNone = {
      renewalChangePercentage: SortDirection.none,
      product: SortDirection.none,
      customer: SortDirection.none,
      customerLastName: SortDirection.none,
      effective: SortDirection.none,
    };
    setSortDirections((prev) => ({
      ...getNone,
      [key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
    }));
    setCurrentSortKey(key);
    setCurrentSortDirection(sortDirections[key]);
  };

  useEffect(() => {
    const start = page * 5;
    setRenewalsTableDataToDisplay(renewalsTableData.slice(start, Math.min(start + 5, renewalsTableData?.length)));
  }, [page]);

  return isLoading ? (
    <Loading />
  ) : initialDataLoad?.length ? (
    <FlexibleContentTile
      data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
      tileClass="renewals-tile"
      children={[
        <div id="renewals-table-container" className="recent-activities-table-container">
          {renewalsTableData?.length ? (
            <>
              <div key={props.activeTab} className="renewals-tab-content tab-content">
                <div className="renewals-chart-container">
                  <PieChart lessThan20={lessThan20} greaterThan20={greaterThan20}></PieChart>
                </div>
                <div className="design-system-data-table-container">
                  <div className="design-system-data-table">
                    <DataTable>
                      <Table className="design-system-table" spacing="micro">
                        <TableHeader>
                          <TableRow>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.customerLastName}
                              textAlign="left"
                              onClick={() => renewalTableSorter(RenewalsSortableKeys.customerLastName)}
                            >
                              Customer
                            </TableCell>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.renewalChangePercentage}
                              textAlign="left"
                              onClick={() => renewalTableSorter(RenewalsSortableKeys.renewalChangePercentage)}
                              className="medium-column-header"
                            >
                              Renewal change
                            </TableCell>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.effective}
                              textAlign="left"
                              onClick={() => renewalTableSorter(RenewalsSortableKeys.effective)}
                              className="small-column-header"
                            >
                              Effective
                            </TableCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody id="renewals-table-body">
                          {renewalsTableDataToDisplay.map((data: IRenewalsTableData, rowIndex: any) => (
                            <TableRow key={`${rowIndex}-renewals`}>
                              <TableCell key={`${rowIndex}-renewals-customer-name`}>
                                <div className="customer-name-container renewals-customer-name">
                                  <div className="renewals-icon">
                                    <GetPolicyIcon policy={data} />
                                  </div>
                                  <Tooltip
                                    className="recent-activities-tile-tooltip"
                                    content={
                                      <Link
                                        target="_blank"
                                        href={
                                          data.product == 'Auto' || data.product == 'Home'
                                            ? getPLMMRLink(userData?.isAgent() ?? true, data.policyNumber)
                                            : getPLPSTLink() + '?Policy=' + data.policyNumber
                                        }
                                      >
                                        {data.customer}
                                      </Link>
                                    }
                                  >
                                    <div className="customer-name">
                                      <Link
                                        target="_blank"
                                        href={
                                          data.product == 'Auto' || data.product == 'Home'
                                            ? getPLMMRLink(userData?.isAgent() ?? true, data.policyNumber)
                                            : getPLPSTLink() + '?Policy=' + data.policyNumber
                                        }
                                      >
                                        {data.customer}
                                      </Link>
                                    </div>
                                  </Tooltip>
                                </div>
                              </TableCell>
                              <TableCell key={`${rowIndex}-renewals-renewal-change`}>
                                <div className="renewal-change-container">
                                  <span className="renewal-change-amount">
                                    {formatRenewalChangeAmount(data.renewalChangeAmount)}{' '}
                                  </span>
                                  <span
                                    className={`renewal-change-percentage ${getRenewalColor(
                                      data.renewalChangePercentage,
                                    )}`}
                                  >
                                    {formatRenewalPercentage(data.renewalChangePercentage)}%
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell
                                key={`${rowIndex}-renewals-effective-date`}
                                className="renewals-table-effective-date"
                              >
                                {formatEffectiveDate(data.effective)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </DataTable>
                    
                    {renewalDocumentCount > 50 && page === 9 ? (
                      <div className="recent-activities-info-view-all">
                        <div className="recent-activities-info">
                          <IconInfo size="16" color="informative" />
                          Viewing only 50 most recent activities.
                          {recentActivitiesLink ? (
                            <Link
                              target="_blank"
                              href={recentActivitiesLink}
                              variant="standalone"
                              className="portal-tile-v2-header-link"
                            >
                              View all activity
                            </Link>
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
              <LMDSPagination />
            </>
          ) : (
            <Loading />
          )}
        </div>,
      ]}
    />
  ) : (
    <RecentActivitiesNoData messageLeft={noDataTile.leftText} messageRight={noDataTile.rightText} />
  );
}
